import { useState } from 'react';

import type { Supplier } from '../interfaces/ILogistics.js';
import {
  draftBlocksSave,
  editSupplierDraft,
  newSupplierDraft,
  supplierById,
  supplierSaveErrorOf,
  type IOrderSupplierChoice,
  type ISupplierDraft,
  type ISupplierFields,
} from '../logistics/suppliers.js';
import { reseed } from '../logistics/orderProvenance.js';

export interface IUseOrderSupplierArgs {
  /** The order's saved supplier (an edit), the suggested one (a create), or null. */
  initialId: string | null;
  suppliers: Supplier[];
  /** The host's last order-write error. One that was already there when the
   *  form mounted belongs to an earlier form and is never shown. */
  saveError: unknown;
}

/**
 * The order form's Supplier (spec 2026-10-25 §12), shared by web and mobile
 * (each renders its own fields): the pick, and the inline draft of a new or
 * edited supplier that the ORDER's Save writes. Cancel discards the draft and
 * leaves the pick as it was; a pick closes the draft. A save refused on the
 * supplier (`taken`, `phone`) lands in `error` with the draft still open, so
 * nothing typed is lost; any change to the draft clears it.
 */
export function useOrderSupplier({ initialId, suppliers, saveError }: IUseOrderSupplierArgs) {
  const [id, setId] = useState(initialId);
  const [draft, setDraft] = useState<ISupplierDraft | null>(null);
  const [seen, setSeen] = useState<unknown>(saveError);
  const picked = supplierById(suppliers, id);
  const choice: IOrderSupplierChoice = { id, draft };

  /** Every draft change leaves the last refusal behind: it was about what the
   *  user has just changed, or about a draft that is gone. */
  const changeDraft = (next: ISupplierDraft | null | ((open: ISupplierDraft | null) => ISupplierDraft | null)) => {
    setDraft(next);
    setSeen(saveError);
  };

  return {
    /** The picked supplier, or null (none, or an id the list no longer holds). */
    picked,
    /** A pick ends any open draft: an edit belongs to the supplier it was
     *  opened on, and a new supplier IS the pick it would replace. */
    pick: (next: string | null) => {
      setId(next);
      changeDraft(null);
    },
    draft,
    /** Opens a new-supplier draft, Company prefilled with `name`. */
    startNew: (name: string) => changeDraft(newSupplierDraft(name)),
    /** Opens an edit draft of the picked supplier. */
    startEdit: (of: Supplier) => changeDraft(editSupplierDraft(of)),
    setField: (field: keyof ISupplierFields, value: string) =>
      changeDraft((open) => (open ? { ...open, [field]: value } : open)),
    cancel: () => changeDraft(null),
    /** A materials change's new suggestion (`suggestOrder`): written only into
     *  a pick still holding the last one, or none — the `reseed` rule every
     *  seeded field follows — and never under an open draft, which is the
     *  user's own answer in progress. */
    offer: (lastSuggested: string, next: string) => {
      if (draft === null) setId((current) => reseed(current ?? '', lastSuggested, next) || null);
    },
    /** The draft has no Company: the order's Save waits. */
    blocksSave: draftBlocksSave(draft),
    error: saveError === seen ? null : supplierSaveErrorOf(saveError),
    /** What `buildSubmitPayload` sends (`supplierPayloadOf`). */
    choice,
  };
}

export type IOrderSupplier = ReturnType<typeof useOrderSupplier>;
